import { yupResolver } from '@hookform/resolvers/yup';
import cn from 'classnames';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import transcriptorMicPreview from '@/shared/assets/images/transcriptor_mic_preview.jpg';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { trackEvent } from '@/shared/lib';
import { Button, Icon, Modal, Select, TabGroup, TabItem } from '@/shared/ui';
import { OrderReportDropzone } from '@/shared/ui';
import { mediaStreamTracker } from '@/shared/utils/mediaStreamTracker';

import { DiagnocoinNotice } from '@/entities/billing';
import { composedModel } from '@/entities/composed';
import { ModalID, modalModel } from '@/entities/modal';
import { organizationModel } from '@/entities/organization';
import { templatesModel } from '@/entities/templates';
import {
  TRANSCRIPTOR_LANGUAGE,
  transcriptorModel,
  useTranscriptorLanguages,
} from '@/entities/transcriptor';

import {
  audioRecordingService,
  RecordingMethod,
  transcriptorRecordingMessages,
  useFileUpload,
  useMicrophonePermissions,
  useSoundCheck,
  useStartRecording,
} from '@/features/audioRecording';

import {
  RecordingStep,
  TranscriptorRecordingFormData,
  transcriptorRecordingSchema,
} from '../../config';
import { useLocalizedTemplates, useTranscriptorPreferences } from '../../hooks';
import { TranscriptorSelectionFields } from '../TranscriptorSelectionFields/TranscriptorSelectionFields';

import styles from './ClassicRecordingModal.module.scss';

export const ClassicRecordingModal: FC = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const [currentStep, setCurrentStep] = useState<RecordingStep>(
    RecordingStep.Initial,
  );
  const cleanupAudioMonitoringRef = useRef<(() => void) | null>(null);
  const wasVisibleRef = useRef(false);

  const { visible, data } = useAppSelector(
    (state) => state.modal[ModalID.TranscriptorRecording],
  );
  const patientID = data?.patientID as string | undefined;
  const continueEncounterID = data?.encounterID as string | undefined;
  const { funding } = useAppSelector(
    composedModel.selectors.selectTranscriptorAvailability,
  );

  const { languageOptions } = useTranscriptorLanguages();

  const { preferences, updatePreferences, resetPreferences } =
    useTranscriptorPreferences(languageOptions);
  const {
    schemaId: preferredSchemaId,
    templateSeriesIds: preferredTemplateSeriesIds,
    language: preferredLanguage,
    recordingMethod: preferredRecordingMethod,
  } = preferences;

  const organizationID = useAppSelector(
    organizationModel.selectors.selectCurrentOrganizationID,
  );
  const {
    schemas: filteredSchemas,
    documentTemplates: filteredDocumentTemplates,
  } = useLocalizedTemplates();
  // Raw count, not filteredSchemas.length — the lazy-load guard must refetch
  // only when no schemas are loaded at all, not when none match the language.
  const loadedSchemasCount = useAppSelector(
    templatesModel.selectors.selectPublishedEncounterSchemas,
  ).length;
  const documentTemplatesLoading = useAppSelector(
    templatesModel.selectors.selectDocumentTemplatesLoading,
  );
  const schemasLoading = useAppSelector(
    templatesModel.selectors.selectEncounterSchemasLoading,
  );

  const { control, reset, watch, setValue } =
    useForm<TranscriptorRecordingFormData>({
      resolver: yupResolver(transcriptorRecordingSchema),
      defaultValues: {
        schemaId: preferredSchemaId,
        templateSeriesIds: preferredTemplateSeriesIds,
        language: preferredLanguage,
        recordingMethod: preferredRecordingMethod,
        selectedDevice: '',
        uploadedFile: null,
      },
    });

  const watchedValues = watch();
  const currentSchemaId = watchedValues.schemaId || preferredSchemaId;
  const currentLanguage = watchedValues.language || preferredLanguage;
  const watchedTemplateSeriesIds = watchedValues.templateSeriesIds;
  const selectedSchema = useMemo(
    () => filteredSchemas.find((schema) => schema.id === currentSchemaId),
    [filteredSchemas, currentSchemaId],
  );
  const selectedSchemaId = selectedSchema?.id;
  // Stored ids can belong to another schema or organization, so only the
  // selected schema's on-demand series may reach the request.
  const currentTemplateSeriesIds = useMemo(() => {
    const onDemandSeriesIds = new Set(
      (selectedSchema?.documents ?? [])
        .filter((doc) => doc.inclusion === 'on_demand')
        .map((doc) => doc.template_series_id),
    );
    const storedSeriesIds = Array.isArray(watchedTemplateSeriesIds)
      ? (watchedTemplateSeriesIds.filter(Boolean) as string[])
      : preferredTemplateSeriesIds;

    return storedSeriesIds.filter((seriesId) =>
      onDemandSeriesIds.has(seriesId),
    );
  }, [selectedSchema, watchedTemplateSeriesIds, preferredTemplateSeriesIds]);
  // A new encounter needs a schema; continuing one reuses the encounter's own.
  const isSchemaMissing = !continueEncounterID && !selectedSchema;

  const selectMenuPortalTarget =
    typeof document !== 'undefined' ? document.body : null;

  const {
    hasPermission,
    isPermanentlyDenied,
    permissionError,
    devices,
    selectedDevice,
    setSelectedDevice,
    audioLevel,
    startAudioLevelMonitoring,
    checkMicrophonePermission,
  } = useMicrophonePermissions();

  const currentSelectedDevice = watchedValues.selectedDevice || selectedDevice;

  const isIosNonSafari = useMemo(() => {
    const ua = navigator.userAgent;
    const isIos =
      /iPad|iPhone|iPod/.test(ua) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS|EdgiOS/.test(ua);

    return isIos && !isSafari;
  }, []);

  const isWakeLockSupported = 'wakeLock' in navigator;
  const isDevelopment = process.env.NODE_ENV === 'development';

  const isSoundCheckActive =
    hasPermission === true &&
    !!selectedDevice &&
    preferredRecordingMethod === RecordingMethod.Record;

  const { soundCheckStatus } = useSoundCheck(audioLevel, isSoundCheckActive);

  const closeModalTemp = useCallback(() => {
    dispatch(modalModel.actions.closeModal(ModalID.TranscriptorRecording));
  }, [dispatch]);

  const {
    isUploading,
    uploadProgress,
    uploadError,
    isProcessing,
    handleFileUpload,
    resetUploadState,
  } = useFileUpload({
    patientId: patientID,
    schemaId: selectedSchemaId,
    selectedOnDemandSeriesIds: currentTemplateSeriesIds,
    language: currentLanguage,
    encounterId: continueEncounterID,
    onClose: closeModalTemp,
  });

  const closeModal = useCallback(async () => {
    if (cleanupAudioMonitoringRef.current) {
      cleanupAudioMonitoringRef.current();
      cleanupAudioMonitoringRef.current = null;
    }

    // Don't stop recording when closing modal - recording continues via RecordingToast.
    // Only clean up streams if NOT recording (e.g., user opened modal but didn't start).
    if (!audioRecordingService.isRecording()) {
      dispatch(transcriptorModel.actions.resetRecording());

      const activeStreams = mediaStreamTracker.getActiveStreams();
      if (activeStreams.length > 0) {
        mediaStreamTracker.stopAllStreams();
      }
    }

    setCurrentStep(RecordingStep.Initial);
    resetPreferences();
    reset();
    resetUploadState();
    dispatch(modalModel.actions.closeModal(ModalID.TranscriptorRecording));
  }, [dispatch, resetPreferences, reset, resetUploadState]);

  const handleClose = useCallback(() => {
    if (isUploading || isProcessing) {
      dispatch(
        modalModel.actions.openModal({
          modalID: ModalID.Confirm,
          data: {
            title: formatMessage(
              transcriptorRecordingMessages.confirmCloseTitle,
            ),
            description: formatMessage(
              transcriptorRecordingMessages.confirmCloseWhileUploading,
            ),
            okText: formatMessage({
              id: 'global.yes',
              defaultMessage: 'Yes',
            }),
            cancelText: formatMessage({
              id: 'global.no',
              defaultMessage: 'No',
            }),
            danger: true,
            onConfirm: () => {
              dispatch(modalModel.actions.closeModal(ModalID.Confirm));
              closeModal();
            },
            onCancel: () => {
              dispatch(modalModel.actions.closeModal(ModalID.Confirm));
            },
          },
        }),
      );
      return;
    }

    closeModal();
  }, [dispatch, isUploading, isProcessing, formatMessage, closeModal]);

  const onDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      if (acceptedFiles.length > 0) {
        trackEvent({
          eventName: 'PatientProfilePageTranscriptorOrderModalUploadStarted',
          properties: {
            languages: currentLanguage,
            schema_id: currentSchemaId,
          },
        });

        setValue('uploadedFile', acceptedFiles[0]);
        handleFileUpload(acceptedFiles[0]);
        dispatch(modalModel.actions.closeModal(ModalID.MobileOrderReportModal));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentLanguage, currentSchemaId, handleFileUpload],
  );

  const dropzoneState = useDropzone({
    onDrop,
    accept: {
      'audio/mpeg': ['.mp3'],
      'audio/mp4': ['.mp4', '.m4a'],
      'audio/wav': ['.wav'],
      'audio/ogg': ['.ogg'],
      'audio/webm': ['.webm'],
      'video/webm': ['.webm'],
    },
    maxFiles: 1,
    multiple: false,
    disabled: isSchemaMissing,
  });

  const { startRecording } = useStartRecording({
    patientID,
    schemaId: selectedSchemaId,
    selectedOnDemandSeriesIds: currentTemplateSeriesIds,
    language: currentLanguage,
    selectedDevice: currentSelectedDevice,
    devices,
    encounterId: continueEncounterID,
    closeModalId: ModalID.TranscriptorRecording,
    onCleanup: cleanupAudioMonitoringRef.current || undefined,
  });

  useEffect(() => {
    const isOpening = visible && !wasVisibleRef.current;
    wasVisibleRef.current = visible;

    if (!isOpening) {
      return;
    }

    reset({
      schemaId: preferredSchemaId,
      templateSeriesIds: preferredTemplateSeriesIds,
      language: preferredLanguage,
      recordingMethod: preferredRecordingMethod,
      selectedDevice: selectedDevice || '',
      uploadedFile: null,
    });

    resetUploadState();

    if (loadedSchemasCount === 0 && !schemasLoading && organizationID) {
      void dispatch(
        templatesModel.fetchEncounterSchemas({
          organization_id: organizationID,
        }),
      );
    }

    if (!documentTemplatesLoading && organizationID) {
      void dispatch(
        templatesModel.fetchDocumentTemplates({
          organization_id: organizationID,
        }),
      );
    }

    if (
      preferredRecordingMethod === RecordingMethod.Record &&
      hasPermission !== true
    ) {
      checkMicrophonePermission();
    }
  }, [
    visible,
    reset,
    resetUploadState,
    selectedDevice,
    checkMicrophonePermission,
    hasPermission,
    dispatch,
    loadedSchemasCount,
    schemasLoading,
    documentTemplatesLoading,
    organizationID,
    preferredSchemaId,
    preferredTemplateSeriesIds,
    preferredLanguage,
    preferredRecordingMethod,
  ]);

  useEffect(() => {
    if (!visible) {
      return;
    }

    setValue('selectedDevice', selectedDevice || '');
  }, [visible, selectedDevice, setValue]);

  // Default schemaId to first available schema if not chosen yet, or if the
  // stored schemaId is no longer in the list (e.g. stale OBSOLETE id from
  // localStorage after the schema was republished).
  useEffect(() => {
    if (!visible || filteredSchemas.length === 0) {
      return;
    }
    const isStoredSchemaAvailable =
      !!currentSchemaId &&
      filteredSchemas.some((schema) => schema.id === currentSchemaId);
    if (isStoredSchemaAvailable) {
      return;
    }
    const firstSchemaId = filteredSchemas[0].id;
    setValue('schemaId', firstSchemaId);
    updatePreferences({ schemaId: firstSchemaId, templateSeriesIds: [] });
  }, [visible, filteredSchemas, currentSchemaId, setValue, updatePreferences]);

  // Auto-pick language from the selected schema — the schema is the single
  // source of truth for the consultation language.
  useEffect(() => {
    if (!selectedSchema) {
      return;
    }
    const schemaLanguage = selectedSchema.language as TRANSCRIPTOR_LANGUAGE;
    const isAvailable = languageOptions.some(
      (option) => option.value === schemaLanguage,
    );
    if (!isAvailable || schemaLanguage === currentLanguage) {
      return;
    }
    setValue('language', schemaLanguage);
    updatePreferences({ language: schemaLanguage });
  }, [
    selectedSchema,
    languageOptions,
    currentLanguage,
    setValue,
    updatePreferences,
  ]);

  const handleSchemaChange = useCallback(
    (nextSchemaId: string) => {
      setValue('templateSeriesIds', []);
      updatePreferences({
        schemaId: nextSchemaId,
        templateSeriesIds: [],
      });
    },
    [setValue, updatePreferences],
  );

  const handleTemplatesChange = useCallback(
    (nextTemplateSeriesIds: string[]) => {
      updatePreferences({ templateSeriesIds: nextTemplateSeriesIds });
    },
    [updatePreferences],
  );

  const handleStartRecordingFromMicrophone = useCallback(async () => {
    if (cleanupAudioMonitoringRef.current) {
      cleanupAudioMonitoringRef.current();
      cleanupAudioMonitoringRef.current = null;
    }

    if (hasPermission !== true) {
      await checkMicrophonePermission();
      return;
    }

    trackEvent({
      eventName:
        'PatientProfilePageTranscriptorOrderModalStartRecordingButtonClicked',
      properties: {
        languages: currentLanguage,
        schema_id: currentSchemaId,
      },
    });

    await startRecording();
    dispatch(modalModel.actions.closeModal(ModalID.MobileOrderReportModal));
  }, [
    dispatch,
    startRecording,
    hasPermission,
    checkMicrophonePermission,
    currentLanguage,
    currentSchemaId,
  ]);

  useEffect(() => {
    if (cleanupAudioMonitoringRef.current) {
      cleanupAudioMonitoringRef.current();
      cleanupAudioMonitoringRef.current = null;
    }

    if (
      !visible ||
      !selectedDevice ||
      hasPermission !== true ||
      currentStep !== RecordingStep.Initial ||
      preferredRecordingMethod !== RecordingMethod.Record
    ) {
      return;
    }

    const setupAudioMonitoring = async () => {
      const cleanup = await startAudioLevelMonitoring(selectedDevice);
      if (cleanup) {
        cleanupAudioMonitoringRef.current = cleanup;
      }
    };

    setupAudioMonitoring();

    return () => {
      if (cleanupAudioMonitoringRef.current) {
        cleanupAudioMonitoringRef.current();
        cleanupAudioMonitoringRef.current = null;
      }
    };
  }, [
    visible,
    selectedDevice,
    hasPermission,
    currentStep,
    preferredRecordingMethod,
    startAudioLevelMonitoring,
  ]);

  const renderNewRecordingContent = () => (
    <>
      <div className={styles.selectionSection}>
        <TranscriptorSelectionFields
          control={control}
          schemas={filteredSchemas}
          documentTemplates={filteredDocumentTemplates}
          schemasLoading={schemasLoading}
          selectedSchemaId={currentSchemaId}
          selectedTemplateSeriesIds={currentTemplateSeriesIds}
          selectMenuPortalTarget={selectMenuPortalTarget}
          onSchemaChange={handleSchemaChange}
          onTemplatesChange={handleTemplatesChange}
        />
      </div>

      <div className={styles.microphoneSection}>
        <h3 className={cn('h7', styles.sectionTitle)}>
          {formatMessage(transcriptorRecordingMessages.microphoneConfigTitle)}
        </h3>
        <p className={cn('p2', styles.description)}>
          {hasPermission === true && !permissionError ? (
            formatMessage(
              transcriptorRecordingMessages.microphoneConfigDescription,
            )
          ) : hasPermission === false ? (
            isPermanentlyDenied ? (
              <>
                {formatMessage(
                  transcriptorRecordingMessages.microphonePermissionPermanentlyDenied,
                )}
                <br />
                <strong>
                  {formatMessage(
                    transcriptorRecordingMessages.microphonePermissionPermanentlyDeniedInstructions,
                  )}
                </strong>
              </>
            ) : (
              formatMessage({
                id: 'transcriptor.microphoneDenied',
                defaultMessage:
                  'Microphone access denied. Click the button below to request again or allow access in browser settings.',
              })
            )
          ) : (
            formatMessage({
              id: 'transcriptor.requestMicrophoneAccess',
              defaultMessage:
                'Click the button below to request microphone access',
            })
          )}
        </p>

        {(hasPermission === null || hasPermission === false) && (
          <div className={styles.actionButton}>
            <Button
              onClick={() => {
                if (isPermanentlyDenied) {
                  window.location.reload();
                } else {
                  checkMicrophonePermission();
                }
              }}
              size="large"
              variant="secondary"
            >
              {hasPermission === false
                ? isPermanentlyDenied
                  ? formatMessage({
                      id: 'global.refreshPage',
                      defaultMessage: 'Refresh page',
                    })
                  : formatMessage({
                      id: 'transcriptor.requestMicrophoneAgainButton',
                      defaultMessage: 'Request microphone access again',
                    })
                : formatMessage({
                    id: 'transcriptor.requestMicrophoneButton',
                    defaultMessage: 'Request microphone access',
                  })}
            </Button>
          </div>
        )}

        {hasPermission === true && devices.length > 0 && (
          <>
            <Controller
              name="selectedDevice"
              control={control}
              render={({ field }) => (
                <Select
                  className={styles.microphoneSelect}
                  isSearchable={false}
                  value={field.value}
                  menuPortalTarget={selectMenuPortalTarget ?? undefined}
                  menuPosition={selectMenuPortalTarget ? 'fixed' : undefined}
                  onChange={(value) => {
                    if (typeof value === 'string' || value === null) {
                      field.onChange(value || '');
                      setSelectedDevice(value || '');
                    }
                  }}
                  options={devices.map((device) => ({
                    value: device.deviceId,
                    label: device.label,
                  }))}
                  placeholder={formatMessage(
                    transcriptorRecordingMessages.microphonePlaceholder,
                  )}
                />
              )}
            />

            {selectedDevice && (
              <>
                <div className={styles.audioLevelContainer}>
                  <div className={cn('p3', styles.audioLevelLabel)}>
                    {formatMessage(
                      transcriptorRecordingMessages.audioLevelLabel,
                    )}
                  </div>
                  <div className={styles.audioLevelBar}>
                    <div
                      className={styles.audioLevelFill}
                      style={{ width: `${Math.min(audioLevel, 100)}%` }}
                    />
                  </div>
                </div>

                {soundCheckStatus !== 'idle' && (
                  <div
                    className={cn('p3', styles.soundCheckStatus, {
                      [styles.soundCheckDetected]:
                        soundCheckStatus === 'detected',
                      [styles.soundCheckNoSound]:
                        soundCheckStatus === 'no-sound',
                    })}
                  >
                    <Icon
                      name={
                        soundCheckStatus === 'detected'
                          ? 'check'
                          : soundCheckStatus === 'no-sound'
                            ? 'alert'
                            : 'info'
                      }
                      size={16}
                    />
                    <span>
                      {soundCheckStatus === 'checking' &&
                        formatMessage(
                          transcriptorRecordingMessages.soundCheckChecking,
                        )}
                      {soundCheckStatus === 'detected' &&
                        formatMessage(
                          transcriptorRecordingMessages.soundCheckDetected,
                        )}
                      {soundCheckStatus === 'no-sound' &&
                        formatMessage(
                          transcriptorRecordingMessages.soundCheckNoSound,
                        )}
                    </span>
                  </div>
                )}
              </>
            )}
          </>
        )}

        <div className={styles.actionButton}>
          <Button
            onClick={handleStartRecordingFromMicrophone}
            disabled={
              isSchemaMissing ||
              !currentLanguage ||
              (hasPermission !== true && !isDevelopment) ||
              (!currentSelectedDevice && !isDevelopment)
            }
            size="large"
          >
            {formatMessage(transcriptorRecordingMessages.startRecordingButton)}
          </Button>
        </div>

        {(isIosNonSafari || !isWakeLockSupported) && (
          <div className={styles.deviceWarnings}>
            {isIosNonSafari && (
              <div className={cn('p3', styles.deviceWarning)}>
                <Icon name="alert" size={16} />
                <span>
                  {formatMessage(
                    transcriptorRecordingMessages.deviceWarningIosSafari,
                  )}
                </span>
              </div>
            )}
            {!isWakeLockSupported && (
              <div className={cn('p3', styles.deviceWarning)}>
                <Icon name="alert" size={16} />
                <span>
                  {formatMessage(
                    transcriptorRecordingMessages.deviceWarningWakeLock,
                  )}
                </span>
              </div>
            )}
          </div>
        )}

        <div className={styles.microphonePlacement}>
          <div className={styles.warningIcon}>
            <Icon name="alert" size={24} />
            <h4 className={cn('bold', styles.warningTitle)}>
              {formatMessage(
                transcriptorRecordingMessages.microphonePlacementTitle,
              )}
            </h4>
          </div>
          <img
            src={transcriptorMicPreview}
            alt={formatMessage(
              transcriptorRecordingMessages.microphonePlacementAlt,
            )}
            className={styles.placementImage}
          />
        </div>
      </div>
    </>
  );

  const renderUploadFileContent = () => (
    <>
      <div className={styles.selectionSection}>
        <TranscriptorSelectionFields
          control={control}
          schemas={filteredSchemas}
          documentTemplates={filteredDocumentTemplates}
          schemasLoading={schemasLoading}
          selectedSchemaId={currentSchemaId}
          selectedTemplateSeriesIds={currentTemplateSeriesIds}
          selectMenuPortalTarget={selectMenuPortalTarget}
          onSchemaChange={handleSchemaChange}
          onTemplatesChange={handleTemplatesChange}
        />
      </div>

      <div className={styles.fileUploadSection}>
        {uploadError && (
          <div className={styles.errorMessage}>
            <Icon name="alert" size={20} />
            <span>{uploadError}</span>
          </div>
        )}

        {isUploading ? (
          <div className={cn('p2', styles.loadingMessage)}>
            <div className={styles.spinner} />
            <span>
              {formatMessage(transcriptorRecordingMessages.uploadingMessage)}
              {uploadProgress > 0 && ` ${uploadProgress}%`}
            </span>
          </div>
        ) : isProcessing ? (
          <div className={cn('p2', styles.loadingMessage)}>
            <div className={styles.spinner} />
            <span>
              {formatMessage(transcriptorRecordingMessages.processingMessage)}
            </span>
          </div>
        ) : (
          <div className={cn({ [styles.dropzoneDisabled]: isSchemaMissing })}>
            <OrderReportDropzone dropzoneState={dropzoneState} />
          </div>
        )}
      </div>
    </>
  );

  const renderInitialView = () => {
    const tabItems: TabItem[] = [
      {
        label: formatMessage(transcriptorRecordingMessages.newRecordingTab),
        value: 'new-recording',
        content: renderNewRecordingContent(),
      },
      {
        label: formatMessage(transcriptorRecordingMessages.uploadFileTab),
        value: 'upload-file',
        content: renderUploadFileContent(),
      },
    ];

    return (
      <div className={styles.initialView}>
        {!continueEncounterID && (
          <DiagnocoinNotice funding={funding} className={styles.coinNotice} />
        )}
        <TabGroup
          tabName="transcriptor-method"
          tabItems={tabItems}
          defaultTabID={
            preferredRecordingMethod === RecordingMethod.Upload
              ? 'upload-file'
              : 'new-recording'
          }
          onTabChange={(tabId) => {
            if (cleanupAudioMonitoringRef.current) {
              cleanupAudioMonitoringRef.current();
              cleanupAudioMonitoringRef.current = null;
            }

            const newMethod =
              tabId === 'upload-file'
                ? RecordingMethod.Upload
                : RecordingMethod.Record;
            setValue('recordingMethod', newMethod);
            updatePreferences({ recordingMethod: newMethod });

            if (newMethod === RecordingMethod.Record && selectedDevice) {
              setValue('selectedDevice', selectedDevice);
            }

            resetUploadState();
          }}
        />
      </div>
    );
  };

  const getModalContent = () => {
    return renderInitialView();
  };

  const getModalTitle = () => {
    return formatMessage(transcriptorRecordingMessages.modalTitle);
  };

  useEffect(() => {
    return () => {
      if (cleanupAudioMonitoringRef.current) {
        cleanupAudioMonitoringRef.current();
        cleanupAudioMonitoringRef.current = null;
      }

      // Don't stop recording or media streams on unmount - recording should
      // continue in background when navigating away from patient page.
      // Recording is controlled via RecordingToast or explicit modal close.
      if (!audioRecordingService.isRecording()) {
        const activeStreams = mediaStreamTracker.getActiveStreams();
        if (activeStreams.length > 0) {
          mediaStreamTracker.stopAllStreams();
        }
      }
    };
  }, []);

  return (
    <Modal
      data-component="transcriptor-recording-modal"
      isOpen={visible}
      onCancel={handleClose}
      title={getModalTitle()}
      containerClassName={styles.modalContainer}
      bodyClassName={styles.modalBody}
      shouldRenderCancelButton={false}
      shouldCloseOnOverlayClick={!isUploading && !isProcessing}
      hideFooter
    >
      <div className={styles.modalContent}>{getModalContent()}</div>
    </Modal>
  );
};
